import { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { panelVariants, transition } from '../lib/motion.js';
import { useParams, useSearchParams } from 'react-router-dom';
import { useEventStore } from '../store/useEventStore.js';
import RequestList from '../components/RequestList.jsx';
import SuggestModal from '../components/SuggestModal.jsx';
import EmptyEvent from '../components/EmptyEvent.jsx';
import EventHeader from '../components/EventHeader.jsx';
import Tabs from '../components/Tabs.jsx';
import RulesPanel from '../components/RulesPanel.jsx';
import AccountModal from '../components/AccountModal.jsx';
import ProfileSheet from '../components/ProfileSheet.jsx';
import AccountStrip from '../components/AccountStrip.jsx';
import EventGate from '../components/EventGate.jsx';
import DonationGoalWidget from '../components/DonationGoalWidget.jsx';
import BoostModal from '../components/BoostModal.jsx';
import AcceptedSongBanner from '../components/AcceptedSongBanner.jsx';
import SocialPodium from '../components/SocialPodium.jsx';
import FeedbackButton from '../components/FeedbackButton.jsx';
import { computePassageChancePercent } from '../lib/passageChance.js';
import { getDisplayName } from '../lib/profile.js';
import { supabase } from '../lib/supabase.js';
import { toast } from '../store/useToast.js';
import { isPaidQueueUiEnabled } from '../lib/features.js';
import { formatEventLoadError } from '../lib/guestCopy.js';

const TABS = [
  { id: 'all', label: 'Suggestions' },
  { id: 'goals', label: 'Cagnotte' },
  { id: 'social', label: 'Podium' },
  { id: 'rules', label: 'Règles' },
];

/** Même logique que formatEurosTight dans DonationGoalWidget (pot = bids payés). */
function formatEuroPotLabel(totalCents) {
  const value = Math.max(0, Number(totalCents) || 0);
  if (value < 100 && value > 0) return `${value} c`;
  return `${(value / 100).toLocaleString('fr-FR', {
    minimumFractionDigits: value % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })} €`;
}

function gateStorageKey(eventId) {
  return `autodj_gate_done_${eventId}`;
}

function readGatePassed(eventId) {
  if (!eventId) return false;
  try {
    return localStorage.getItem(gateStorageKey(eventId)) === '1';
  } catch {
    return false;
  }
}

function writeGatePassed(eventId) {
  if (!eventId) return;
  try {
    localStorage.setItem(gateStorageKey(eventId), '1');
  } catch {
    /* private mode */
  }
}

export default function EventPage() {
  const paidQueueUiEnabled = isPaidQueueUiEnabled();
  const { qrCode } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const loading = useEventStore((s) => s.loading);
  const error = useEventStore((s) => s.error);
  const event = useEventStore((s) => s.event);
  const establishment = useEventStore((s) => s.establishment);
  const requests = useEventStore((s) => s.requests);
  const liveStatus = useEventStore((s) => s.liveStatus);
  const participants = useEventStore((s) => s.participants);
  const donationGoal = useEventStore((s) => s.donationGoal);
  const donationGoalPulse = useEventStore((s) => s.donationGoalPulse);
  const eventPotCents = useEventStore((s) => s.eventPotCents);
  const loadEvent = useEventStore((s) => s.loadEvent);
  const resyncEventData = useEventStore((s) => s.resyncEventData);
  const reset = useEventStore((s) => s.reset);
  const liveBoostFeed = useEventStore((s) => s.liveBoostFeed);
  const topContributors = useEventStore((s) => s.topContributors);
  const votePodium = useEventStore((s) => s.votePodium);
  const reactionCountsByRequest = useEventStore((s) => s.reactionCountsByRequest);
  const featuredBadgeByBarUserId = useEventStore((s) => s.featuredBadgeByBarUserId);
  const eventBadges = useEventStore((s) => s.eventBadges);
  const acceptedSong = useEventStore((s) => s.acceptedSong);
  const dismissAcceptedSong = useEventStore((s) => s.dismissAcceptedSong);
  const updateBarUsername = useEventStore((s) => s.updateBarUsername);
  const initBarIdentity = useEventStore((s) => s.initBarIdentity);
  const pruneStaleDonorFeed = useEventStore((s) => s.pruneStaleDonorFeed);
  const barUser = useEventStore((s) => s.barUser);
  const barAuthReady = useEventStore((s) => s.barAuthReady);
  const barAuthReason = useEventStore((s) => s.barAuthReason);
  const barAuthSignInError = useEventStore((s) => s.barAuthSignInError);
  const barAuthProfileError = useEventStore((s) => s.barAuthProfileError);
  const authEmail = useEventStore((s) => s.authEmail);

  const [openModal, setOpenModal] = useState(false);
  // Onglet dans l'URL (?tab=) : le bouton retour du navigateur revient à
  // l'onglet précédent au lieu de quitter la soirée.
  const rawTab = searchParams.get('tab');
  const tab = ['all', 'goals', 'social', 'rules'].includes(rawTab) ? rawTab : 'all';
  const setTab = useCallback(
    (next) => {
      const p = new URLSearchParams(searchParams);
      if (!next || next === 'all') p.delete('tab');
      else p.set('tab', next);
      setSearchParams(p, { replace: false });
    },
    [searchParams, setSearchParams]
  );
  const [accountOpen, setAccountOpen] = useState(false);
  const [accountDefaultView, setAccountDefaultView] = useState('home');
  const [profileSheet, setProfileSheet] = useState({ open: false, userId: null });
  const [displayName, setDisplayNameState] = useState(getDisplayName());
  const [boostRequest, setBoostRequest] = useState(null);
  const [gatePassed, setGatePassed] = useState(false);
  const reduceMotion = useReducedMotion();

  const navTabs = useMemo(
    () => (paidQueueUiEnabled ? TABS : TABS.filter((t) => t.id !== 'goals')),
    [paidQueueUiEnabled]
  );

  const openAccount = useCallback((view = 'home') => {
    setAccountDefaultView(view);
    setAccountOpen(true);
  }, []);

  const closeAccount = useCallback(() => {
    setAccountOpen(false);
    setAccountDefaultView('home');
  }, []);

  const openProfile = useCallback((userId = null) => {
    setAccountOpen(false);
    setProfileSheet({ open: true, userId });
  }, []);

  const closeProfile = useCallback(() => setProfileSheet({ open: false, userId: null }), []);

  const passEventGate = useCallback(() => {
    if (event?.id) writeGatePassed(event.id);
    setGatePassed(true);
  }, [event?.id]);

  useEffect(() => {
    if (!paidQueueUiEnabled && tab === 'goals') setTab('all');
  }, [paidQueueUiEnabled, tab]);

  useEffect(() => {
    const s = searchParams.get('stripe_checkout');
    if (!paidQueueUiEnabled) {
      if (s === 'success' || s === 'cancel') {
        const next = new URLSearchParams(searchParams);
        next.delete('stripe_checkout');
        next.delete('session_id');
        setSearchParams(next, { replace: true });
      }
      return;
    }
    if (s === 'success') {
      // Le retour d'URL ne prouve pas l'encaissement (webhook Stripe = source de
      // vérité) : on reste prudent, la cagnotte/le boost s'actualisent en realtime
      // dès que le paiement est confirmé côté serveur.
      toast.success('Merci ! Ta contribution s’affichera dès confirmation du paiement.');
      const next = new URLSearchParams(searchParams);
      next.delete('stripe_checkout');
      next.delete('session_id');
      setSearchParams(next, { replace: true });
    } else if (s === 'cancel') {
      toast.error('Paiement annulé');
      const next = new URLSearchParams(searchParams);
      next.delete('stripe_checkout');
      next.delete('session_id');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, paidQueueUiEnabled]);

  useEffect(() => {
    if (qrCode) loadEvent(qrCode);
    return () => reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrCode]);

  useEffect(() => {
    if (!event?.id) {
      setGatePassed(false);
      return;
    }
    setGatePassed(readGatePassed(event.id));
  }, [event?.id]);

  useEffect(() => {
    const nm = barUser?.username ?? getDisplayName();
    setDisplayNameState(nm || '');
  }, [barUser?.username]);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      void initBarIdentity();
    });
    return () => subscription.unsubscribe();
  }, [initBarIdentity]);

  useEffect(() => {
    if (!event?.id || event?.status !== 'live' || !gatePassed) return;
    pruneStaleDonorFeed();
    const tid = setInterval(() => pruneStaleDonorFeed(), 45_000);
    return () => clearInterval(tid);
  }, [event?.id, event?.status, gatePassed, pruneStaleDonorFeed]);

  const stats = useMemo(() => {
    const total = requests.length;
    const boosted = requests.filter((r) => r.is_boosted || (r.bid_amount ?? 0) >= 500).length;
    const tipsLabel = formatEuroPotLabel(eventPotCents);
    return { total, boosted, tipsLabel };
  }, [requests, eventPotCents]);

  const reactionTotals = useMemo(() => {
    const out = {};
    for (const r of requests) {
      const o = reactionCountsByRequest[r.id];
      if (!o) {
        out[r.id] = 0;
        continue;
      }
      out[r.id] = Object.values(o).reduce((a, b) => a + (Number(b) || 0), 0);
    }
    return out;
  }, [requests, reactionCountsByRequest]);

  const passagePercents = useMemo(
    () => computePassageChancePercent(requests, { reactionTotals }),
    [requests, reactionTotals]
  );

  if (loading) {
    return (
      <div className="container-mobile flex min-h-dvh flex-col items-center justify-center text-white/60">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/10 border-t-accent" />
        <p className="mt-3 text-sm">Connexion à la soirée…</p>
      </div>
    );
  }

  if (error) {
    return <EmptyEvent message={formatEventLoadError(error)} onRetry={() => loadEvent(qrCode)} />;
  }

  if (!event || event.status !== 'live') {
    return (
      <EmptyEvent
        message={
          establishment
            ? `Aucune soirée en cours chez ${establishment.name}. Reviens plus tard !`
            : 'Ce QR code ne correspond à aucun établissement reconnu. Vérifie que tu scannes bien le QR du bar.'
        }
        onRetry={qrCode ? () => loadEvent(qrCode) : undefined}
      />
    );
  }

  if (!gatePassed) {
    return (
      <>
        <EventGate
          event={event}
          barAuthReady={barAuthReady}
          barUser={barUser}
          barAuthReason={barAuthReason}
          barAuthSignInError={barAuthSignInError}
          barAuthProfileError={barAuthProfileError}
          onEnterSoiree={passEventGate}
          onOpenAccountAdvanced={() => openAccount('home')}
          onRetryAuth={() => void initBarIdentity()}
          paidQueueUiEnabled={paidQueueUiEnabled}
        />
        <AccountModal
          open={accountOpen}
          onClose={closeAccount}
          defaultView={accountDefaultView}
          barUser={barUser}
          authEmail={authEmail}
          barAuthReady={barAuthReady}
          barAuthReason={barAuthReason}
          barAuthProfileError={barAuthProfileError}
          updateBarUsername={updateBarUsername}
          initBarIdentity={initBarIdentity}
          initialPseudo={displayName ?? ''}
          paidQueueUiEnabled={paidQueueUiEnabled}
          onOpenProfile={() => openProfile(null)}
        />
        <AcceptedSongBanner
          accepted={acceptedSong}
          onDismiss={() => dismissAcceptedSong()}
          paidQueueUiEnabled={paidQueueUiEnabled}
        />
        <ProfileSheet
          open={profileSheet.open}
          onClose={closeProfile}
          profileUserId={profileSheet.userId}
          onManageAccount={() => {
            closeProfile();
            openAccount('home');
          }}
        />
      </>
    );
  }

  return (
    <div className="container-mobile flex min-h-dvh flex-col pb-32 pt-4">
      <motion.div
        initial={reduceMotion ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={transition(reduceMotion, 'panel')}
      >
        <EventHeader
          event={event}
          liveStatus={liveStatus}
          participants={participants}
          displayName={displayName}
          accountLoading={!barAuthReady}
          onOpenAccount={() => openAccount('home')}
          stats={stats}
          liveBoostFeed={liveBoostFeed}
          paidQueueUiEnabled={paidQueueUiEnabled}
        />
      </motion.div>

      {liveStatus === 'reconnecting' ? (
        <div className="mt-3 flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3.5 py-2.5">
          <span className="inline-flex h-2 w-2 shrink-0 animate-pulse rounded-full bg-amber-300" />
          <p className="min-w-0 flex-1 text-xs leading-snug text-amber-100/90">
            Connexion instable — la file peut être figée un instant.
          </p>
          <button
            type="button"
            onClick={() => void resyncEventData()}
            className="shrink-0 rounded-lg border border-amber-400/40 px-3 py-1.5 text-xs font-semibold text-amber-100 hover:bg-amber-400/10"
          >
            Actualiser
          </button>
        </div>
      ) : null}

      {!barUser ? (
        <AccountStrip
          ready={barAuthReady}
          barUser={barUser}
          authEmail={authEmail}
          reason={barAuthReason}
          profileError={barAuthProfileError}
          onOpen={() => openAccount('home')}
          onRetry={() => void initBarIdentity()}
        />
      ) : null}

      <Tabs tabs={navTabs} value={tab} onChange={setTab} />

      <AnimatePresence mode="wait" initial={false}>
        <motion.section
          key={tab}
          className="mt-2"
          variants={panelVariants}
          initial={reduceMotion ? false : 'initial'}
          animate="animate"
          exit={reduceMotion ? undefined : 'exit'}
          transition={transition(reduceMotion, 'medium')}
        >
          {tab === 'rules' ? (
            <RulesPanel paidQueueUiEnabled={paidQueueUiEnabled} />
          ) : tab === 'goals' ? (
            <DonationGoalWidget
              goal={donationGoal}
              pulseToken={donationGoalPulse}
              topContributors={topContributors}
            />
          ) : tab === 'social' ? (
            <SocialPodium
              votePodium={votePodium}
              topContributors={topContributors}
              liveBoostFeed={liveBoostFeed}
              participants={participants}
              eventBadges={eventBadges}
              featuredBadgeByBarUserId={featuredBadgeByBarUserId}
              onOpenSuggest={() => setOpenModal(true)}
              onOpenUserProfile={(uid) => openProfile(uid)}
              paidQueueUiEnabled={paidQueueUiEnabled}
            />
          ) : (
            <RequestList
              requests={requests}
              passagePercents={passagePercents}
              onBoost={paidQueueUiEnabled ? (req) => setBoostRequest(req) : undefined}
              onOpenSuggest={() => setOpenModal(true)}
              paidQueueUiEnabled={paidQueueUiEnabled}
            />
          )}
        </motion.section>
      </AnimatePresence>

      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center bg-gradient-to-t from-bg via-bg/95 to-transparent pb-[max(env(safe-area-inset-bottom),16px)] pt-10">
        <div className="container-mobile pointer-events-auto">
          <button
            type="button"
            onClick={() => setOpenModal(true)}
            className="btn-primary min-h-[52px] w-full py-3.5 text-sm font-semibold"
          >
            + Suggérer une musique
          </button>
        </div>
      </div>

      <FeedbackButton eventId={event.id} barUserId={barUser?.id ?? null} />

      <SuggestModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onRequestProfile={() => openProfile(null)}
      />

      {paidQueueUiEnabled ? (
        <BoostModal
          open={Boolean(boostRequest)}
          request={boostRequest}
          onClose={() => setBoostRequest(null)}
        />
      ) : null}

      <AccountModal
        open={accountOpen}
        onClose={closeAccount}
        defaultView={accountDefaultView}
        barUser={barUser}
        authEmail={authEmail}
        barAuthReady={barAuthReady}
        barAuthReason={barAuthReason}
        barAuthProfileError={barAuthProfileError}
        updateBarUsername={updateBarUsername}
        initBarIdentity={initBarIdentity}
        initialPseudo={displayName ?? ''}
        paidQueueUiEnabled={paidQueueUiEnabled}
        onOpenProfile={() => openProfile(null)}
      />

      <AcceptedSongBanner
        accepted={acceptedSong}
        onDismiss={() => dismissAcceptedSong()}
        paidQueueUiEnabled={paidQueueUiEnabled}
      />

      <ProfileSheet
        open={profileSheet.open}
        onClose={closeProfile}
        profileUserId={profileSheet.userId}
        onManageAccount={() => {
          closeProfile();
          openAccount('home');
        }}
      />
    </div>
  );
}
